import { useEffect, useReducer, useRef, type ReactNode } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

import { cn } from "../lib/cn";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastTone = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

type ToastAction =
  | { type: "add"; item: ToastItem }
  | { type: "remove"; id: number };

// ─── Global store (module-level, no React) ────────────────────────────────────

let nextId = 0;
const listeners = new Set<(action: ToastAction) => void>();

function dispatch(action: ToastAction) {
  for (const listener of listeners) {
    listener(action);
  }
}

export function toast(message: string, tone: ToastTone = "success") {
  const id = ++nextId;
  dispatch({ type: "add", item: { id, message, tone } });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

function toastReducer(state: ToastItem[], action: ToastAction): ToastItem[] {
  if (action.type === "add") {
    return [...state, action.item];
  }
  return state.filter((t) => t.id !== action.id);
}

function useToastStore(): [ToastItem[], (id: number) => void] {
  const [items, dispatch2] = useReducer(toastReducer, []);

  useEffect(() => {
    const listener = (action: ToastAction) => dispatch2(action);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const dismiss = (id: number) => dispatch2({ type: "remove", id });
  return [items, dismiss];
}

// ─── Single toast item ────────────────────────────────────────────────────────

const toneMap = {
  success: {
    icon: CheckCircle2,
    className: "border-border bg-card text-foreground",
    iconClass: "text-[var(--success)]",
  },
  error: {
    icon: AlertCircle,
    className: "border-destructive/20 bg-card text-foreground",
    iconClass: "text-destructive",
  },
  info: {
    icon: Info,
    className: "border-border bg-card text-foreground",
    iconClass: "text-muted-foreground",
  },
} satisfies Record<
  ToastTone,
  { icon: React.ComponentType<{ className?: string }>; className: string; iconClass: string }
>;

const DURATION_MS = 3500;
const ANIMATE_MS = 200;

function ToastEntry({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: number) => void;
}) {
  const { icon: Icon, className, iconClass } = toneMap[item.tone];
  const [visible, setVisible] = useReducer(() => false, true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTimer = () => {
    timerRef.current = setTimeout(() => {
      setVisible();
      setTimeout(() => onDismiss(item.id), ANIMATE_MS);
    }, DURATION_MS);
  };

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, []);

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg transition-all",
        className,
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-2 opacity-0 pointer-events-none",
      )}
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
      role="status"
      style={{ transitionDuration: `${ANIMATE_MS}ms` }}
    >
      <Icon className={cn("size-4 shrink-0", iconClass)} />
      <p className="flex-1 leading-snug">{item.message}</p>
      <button
        aria-label="Dismiss"
        className="ml-1 shrink-0 opacity-50 transition-opacity hover:opacity-100"
        onClick={() => {
          clearTimer();
          setVisible();
          setTimeout(() => onDismiss(item.id), ANIMATE_MS);
        }}
        type="button"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

// ─── Toaster (mount once in layout) ──────────────────────────────────────────

export function Toaster() {
  const [items, dismiss] = useToastStore();

  if (items.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-5 right-5 z-50 flex w-[320px] max-w-[calc(100vw-2.5rem)] flex-col gap-2"
    >
      {items.map((item) => (
        <div className="pointer-events-auto" key={item.id}>
          <ToastEntry item={item} onDismiss={dismiss} />
        </div>
      ))}
    </div>
  );
}
